import React, { useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useQueryLoader } from 'react-relay';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import { NOT_FOUND } from '@atlassian/jira-common-constants/src/http-status-codes';
import { fg } from '@atlassian/jira-feature-gating';
import FetchError, { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import QUERY, {
	type settingsFormV2Query,
} from '@atlassian/jira-relay/src/__generated__/settingsFormV2Query.graphql';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { ConfigType } from '../../common/types';
import { useGetWidgetSettings } from '../../services/use-get-widget-settings';
import { useSaveWidgetSettings } from '../../services/use-save-widget-settings';
import { useValidateRequestType } from '../../services/use-validate-request-type';
import { EmbedCode } from './embed-code';
import FieldsError from './error-sections/fields-error/index.tsx';
import PreconditionError from './error-sections/precondition-error/index.tsx';
import RequestTypeError from './error-sections/request-type-error/index.tsx';
import messages from './messages';
import PreviewWrapper from './preview-wrapper';
import SettingsForm from './settings-form';
import SettingsFormV2 from './settings-form-v2';
import { SUBMIT_CREATED, SUBMIT_UPDATED } from './settings-form/constants';
import type { SubmitFunction } from './settings-form/types';

export const Content = ({ projectId, projectKey }: { projectId: number; projectKey: string }) => {
	const [config, setConfig] = useState<ConfigType | undefined>();
	const [widgetSettingsState, fetchWidgetSettings] = useGetWidgetSettings(projectId);
	const [saveWidgetSettingsState, saveWidgetSettings] = useSaveWidgetSettings(
		projectId,
		projectKey,
	);
	const [embedFormAsIs, setEmbedFormAsIs] = useState(false);
	const [isEnableDisabled, setIsEnableDisabled] = useState(false);
	const [validateRequestTypeState, validateRequestType] = useValidateRequestType(
		projectKey,
		projectId,
		config?.widgetType,
	);

	const { formatMessage } = useIntl();

	const { cloudId } = useTenantContext();
	const [queryReference, loadVaConfigQuery] = useQueryLoader<settingsFormV2Query>(QUERY);
	const isNewWidgetSettingsExperienceEnabled = fg('widget_fe_virtual_agent_enabled');
	const [isFormSubmitSuccess, setIsFormSubmitSuccess] = useState(false);

	useEffect(() => {
		fetchWidgetSettings();
		isNewWidgetSettingsExperienceEnabled &&
			loadVaConfigQuery({
				cloudId,
				projectKey,
			});
	}, [
		fetchWidgetSettings,
		loadVaConfigQuery,
		cloudId,
		projectKey,
		isNewWidgetSettingsExperienceEnabled,
	]);

	useEffect(() => {
		if (config?.requestTypeId) {
			validateRequestType(config.requestTypeId);
		}
	}, [config?.requestTypeId, validateRequestType]);

	useEffect(() => {
		if (
			validateRequestTypeState.data &&
			validateRequestTypeState.data.unsupportedOptionalFields.length === 0
		) {
			if (
				validateRequestTypeState.data.unsupportedRequiredFields.length === 0 ||
				config?.widgetType === 'VIRTUAL_AGENT'
			) {
				setEmbedFormAsIs(true);
				setIsEnableDisabled(false);
			} else {
				setEmbedFormAsIs(false);
				setIsEnableDisabled(true);
			}
		}
	}, [
		validateRequestTypeState.data,
		validateRequestTypeState.data?.unsupportedOptionalFields,
		config?.widgetType,
	]);

	useEffect(() => {
		if (widgetSettingsState.data) {
			if (widgetSettingsState.data.configuration) {
				setConfig(widgetSettingsState.data.configuration);
			}
			if (
				widgetSettingsState.data.validation.jsdPrecondition.length ||
				widgetSettingsState.data.validation.requestTypeValid === false
			) {
				setIsEnableDisabled(true);
			} else {
				setIsEnableDisabled(false);
			}
		}
	}, [widgetSettingsState.data]);

	const onSubmit: SubmitFunction = async (newConfig) => {
		if (widgetSettingsState.data?.key) {
			const response = await saveWidgetSettings({
				...widgetSettingsState.data,
				configuration: newConfig,
			});
			setIsFormSubmitSuccess(true);
			return [response, SUBMIT_UPDATED];
		}
		await saveWidgetSettings(newConfig);
		const response = await fetchWidgetSettings();
		setIsFormSubmitSuccess(true);
		return [response, SUBMIT_CREATED];
	};

	const isConfigChanged = useMemo(() => {
		if (config && widgetSettingsState.data?.configuration) {
			if (widgetSettingsState.data === null) {
				return false;
			}
			if (saveWidgetSettingsState.data?.configuration) {
				return !isEqual(
					{
						...config,
						serviceDeskId: saveWidgetSettingsState.data.configuration.serviceDeskId,
					},
					saveWidgetSettingsState.data.configuration,
				);
			}
			return !isEqual(
				{ ...config, serviceDeskId: widgetSettingsState.data.configuration.serviceDeskId },
				widgetSettingsState.data.configuration,
			);
		}
		return false;
	}, [config, saveWidgetSettingsState.data?.configuration, widgetSettingsState.data]);

	const showEmbedCode = useMemo(
		() =>
			widgetSettingsState.data?.embeddableSnippet &&
			!widgetSettingsState.loading &&
			!saveWidgetSettingsState.error &&
			!widgetSettingsState.data?.validation.jsdPrecondition.length &&
			embedFormAsIs &&
			!isConfigChanged,
		[widgetSettingsState, saveWidgetSettingsState, embedFormAsIs, isConfigChanged],
	);

	if (
		widgetSettingsState.error &&
		!(
			widgetSettingsState.error instanceof FetchError &&
			widgetSettingsState.error.statusCode === NOT_FOUND
		)
	) {
		return (
			<Box xcss={errorContainerStyles}>
				<SectionMessage appearance="error">
					{formatMessage(messages.errorFetchingSavedConfig)}
				</SectionMessage>
			</Box>
		);
	}

	if (
		widgetSettingsState.loading ||
		widgetSettingsState.data === undefined ||
		(config === undefined && !(widgetSettingsState.data === null))
	) {
		return (
			<Box xcss={loadingContainerStyles}>
				<Spinner size="large" testId="servicedesk-widget-settings.ui.content.spinner" />
			</Box>
		);
	}

	const renderPreconditionError = () => {
		if (widgetSettingsState.data?.validation.jsdPrecondition.length) {
			return <PreconditionError alerts={widgetSettingsState.data.validation.jsdPrecondition} />;
		}
		if (validateRequestTypeState.data?.jsdPrecondition.length) {
			return <PreconditionError alerts={validateRequestTypeState.data.jsdPrecondition} />;
		}
	};
	return (
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="widgetSettingsContent">
			<Flex gap="space.300" direction="column">
				{renderPreconditionError()}

				{validateRequestTypeState.data?.requestTypeValid === false && (
					<RequestTypeError
						restricted={validateRequestTypeState.data?.requestTypeRestricted === true}
					/>
				)}
				<Flex gap="space.600">
					<Box xcss={formBoxStyles}>
						{isNewWidgetSettingsExperienceEnabled ? (
							<SettingsFormV2
								config={config}
								onChange={setConfig}
								onSubmit={onSubmit}
								submitting={saveWidgetSettingsState.loading}
								isFormSubmitSuccess={isFormSubmitSuccess}
								handleSuccessDismiss={setIsFormSubmitSuccess}
								projectKey={projectKey}
								isEnableDisabled={isEnableDisabled}
								isRequestTypeValidationLoading={validateRequestTypeState.loading}
								embedCode={widgetSettingsState.data?.embeddableSnippet}
								queryReference={queryReference}
							/>
						) : (
							<SettingsForm
								config={config}
								onChange={setConfig}
								onSubmit={onSubmit}
								submitting={saveWidgetSettingsState.loading}
								projectKey={projectKey}
								isEnableDisabled={isEnableDisabled}
								isRequestTypeValidationLoading={validateRequestTypeState.loading}
							/>
						)}
					</Box>
					<PreviewWrapper
						config={config}
						projectKey={projectKey}
						projectId={projectId}
						baseUrl={widgetSettingsState.data?.baseUrl || window?.location?.origin}
					/>
				</Flex>
				{!fg('widget_fe_virtual_agent_enabled') && showEmbedCode && (
					<EmbedCode embedCode={widgetSettingsState.data.embeddableSnippet} />
				)}
				<FieldsError
					validation={validateRequestTypeState.data}
					onEmbedFormAsIs={() => setEmbedFormAsIs(true)}
					embedFormAsIs={embedFormAsIs}
				/>

				{validateRequestTypeState.error && (
					<SectionMessage appearance="error">
						{formatMessage(messages.errorValidatingRequestType)}
					</SectionMessage>
				)}
				{saveWidgetSettingsState.error &&
					// we are capturing this error in validation
					!(saveWidgetSettingsState.error instanceof ValidationError) && (
						<SectionMessage appearance="error">
							{formatMessage(messages.errorSavingConfig)}
						</SectionMessage>
					)}
			</Flex>

			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const formBoxStyles = xcss({
	maxWidth: '623px',
	minWidth: '250px',
	width: '100%',
});

const loadingContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	minHeight: 'size.1000',
});

const errorContainerStyles = xcss({
	paddingTop: 'space.100',
});
